/**
 * Turg'un rejim: continuous — moddiy va issiqlik balansi; batch — analitik "loyihaviy" hisob.
 *
 * MANBA: Kasatkin 15-bob (nazariy va haqiqiy quritgich, I–x diagramma bo'yicha hisob);
 *        Pavlov-Romankov-Noskov, "Quritish" misollari.
 *
 * CONTINUOUS
 *   Kalorifer:   Y1 = Y0,  Q_heater = L·(I1 − I0)
 *   Moddiy:      W = G_dry·(X1 − X2) = L·(Y2 − Y1)
 *   Issiqlik:    I2 = I1 − Q_loss/L,  Q_loss = heatLossFraction·Q_heater  (0 → nazariy, I2 = I1)
 *   Chiqish:     T2 = T(I2, Y2),  φ2 = φ(T2, Y2, P)
 *   Solishtirma: l = L/W,  q = Q_heater/W
 *   To'yinish:   φ2 > 1 bo'lsa havo faqat Y_sat gacha suv olishi mumkin: T2s adiabatik to'yinish
 *                harorati (I(T2s, Y_sat(T2s)) = I2), Y2 = Y_sat(T2s), W = L·(Y2 − Y1),
 *                X2actual = X1 − W/G_dry  (material talab darajasida quritilmaydi).
 *
 * OGOHLANTIRISH KODLARI
 *   dryer.noAirflow        critical  L = 0 → quritish yo'q (W = 0)
 *   dryer.saturatedAir     warning   havo to'yinadi (φ2 ≥ 1), φ2 = 1 ga cheklandi, X2actual > X2
 *   dryer.belowEquilibrium warning   X2 < X* talab qilinmoqda; X2 = X* deb olinadi
 *   dryer.overheating      warning   T1 > maxMaterialTemperature
 *   dryer.noDrying         info      T1 ≤ T_wb yoki X1 ≤ X2 — bug'lanish bo'lmaydi (W ≤ 0)
 *
 * ASSUMPTION (psychrometrics.ts dagilarga qo'shimcha):
 *   1. Material va uning suvi issiqlik balansiga kirmaydi (Kasatkin Δ = 0, faqat q_yo'q).
 *   2. Havo quritgichda ideal aralashmaydi/aralashadi degan farq yo'q — faqat kirish/chiqish balansi.
 */
import { getSubstance, heatOfVaporization, saturationTemperature } from '../../properties';
import { bisection } from '../../solvers/bisection';
import { warn, type SolverDiagnostics, type SteadyResult, type Warning } from '../../types';
import {
  constantDryingRate,
  heatTransferCoefficientTreybal,
  timeToMoisture,
  type KineticsParams,
} from './kinetics';
import {
  dewPoint,
  humidEnthalpy,
  humidityRatio,
  relativeHumidity,
  saturationHumidity,
  temperatureFromEnthalpy,
  wetBulbTemperature,
} from './psychrometrics';
import type {
  DryerBatchDesignOutputs,
  DryerContinuousOutputs,
  DryerInputs,
  DryerParams,
  DryerSteadyOutputs,
  HumidAirState,
} from './types';

export function validateParams(p: DryerParams): void {
  if (!(p.pressure > 0)) throw new Error('convective-dryer: pressure must be > 0');
  if (p.heatLossFraction !== undefined && !(p.heatLossFraction >= 0 && p.heatLossFraction < 1))
    throw new Error('convective-dryer: heatLossFraction must be in [0, 1)');
  if (p.equilibriumMoisture !== undefined && !(p.equilibriumMoisture >= 0))
    throw new Error('convective-dryer: equilibriumMoisture must be ≥ 0');
  if (p.mode === 'batch') {
    if (!(p.criticalMoisture !== undefined && p.criticalMoisture > (p.equilibriumMoisture ?? 0)))
      throw new Error('convective-dryer: batch requires criticalMoisture > equilibriumMoisture');
    if (!(p.dryingArea !== undefined && p.dryingArea > 0))
      throw new Error('convective-dryer: batch requires dryingArea > 0');
    if (p.heatTransferCoefficient !== undefined && !(p.heatTransferCoefficient > 0))
      throw new Error('convective-dryer: heatTransferCoefficient must be > 0');
  }
}

export function validateInputs(p: DryerParams, i: DryerInputs): void {
  if (!(i.drySolid >= 0)) throw new Error('convective-dryer: drySolid must be ≥ 0');
  if (!(i.X1 >= 0 && i.X2 >= 0)) throw new Error('convective-dryer: X1, X2 must be ≥ 0');
  if (!(i.airFlow >= 0)) throw new Error('convective-dryer: airFlow must be ≥ 0');
  if (!(i.T0 > 0 && i.T1 > 0)) throw new Error('convective-dryer: T0, T1 must be > 0 K');
  if (!(i.phi0 >= 0 && i.phi0 <= 1)) throw new Error('convective-dryer: phi0 must be in [0, 1]');
  if (p.mode === 'batch' && p.heatTransferCoefficient === undefined) {
    if (!(i.airMassVelocity !== undefined && i.airMassVelocity > 0))
      throw new Error(
        'convective-dryer: batch needs heatTransferCoefficient (params) or airMassVelocity (inputs)',
      );
  }
}

function airState(T: number, Y: number, P: number, warnings?: Warning[]): HumidAirState {
  return { T, Y, phi: relativeHumidity(T, Y, P, warnings), I: humidEnthalpy(T, Y) };
}

/** Kalorifer: 0 → 1 (Y o'zgarmas) */
export function heaterStates(
  p: DryerParams,
  i: DryerInputs,
  warnings?: Warning[],
): { air0: HumidAirState; air1: HumidAirState; Qheater: number } {
  const Y0 = humidityRatio(i.T0, i.phi0, p.pressure, warnings);
  if (!Number.isFinite(Y0))
    throw new Error('convective-dryer: phi0·Ps(T0) ≥ P (ambient air impossible)');
  const air0 = airState(i.T0, Y0, p.pressure, warnings);
  const air1 = airState(i.T1, Y0, p.pressure, warnings);
  return { air0, air1, Qheater: i.airFlow * (air1.I - air0.I) };
}

const OK: SolverDiagnostics = { converged: true, iterations: 0, residual: 0 };

export function solveContinuous(
  p: DryerParams,
  i: DryerInputs,
  warnings: Warning[],
): { outputs: DryerContinuousOutputs; diagnostics: SolverDiagnostics } {
  const P = p.pressure;
  const Xeq = p.equilibriumMoisture ?? 0;
  const { air0, air1, Qheater } = heaterStates(p, i, warnings);
  const L = i.airFlow;
  const G = i.drySolid;

  if (p.maxMaterialTemperature !== undefined && i.T1 > p.maxMaterialTemperature)
    warn(warnings, 'dryer.overheating', 'warning', {
      T1: Math.round(i.T1),
      Tmax: Math.round(p.maxMaterialTemperature),
    });

  let X2 = i.X2;
  if (X2 < Xeq) {
    warn(warnings, 'dryer.belowEquilibrium', 'warning', { X2: i.X2, Xeq });
    X2 = Xeq;
  }
  const wb = wetBulbTemperature(air1.T, air1.Y, P, warnings);
  let diagnostics: SolverDiagnostics = { ...OK, iterations: wb.iterations };

  let W = Math.max(0, G * (i.X1 - X2));
  if (i.X1 <= X2 || i.T1 <= wb.T + 1e-9) {
    warn(warnings, 'dryer.noDrying', 'info');
    W = 0;
  }
  const Qloss = (p.heatLossFraction ?? 0) * Qheater;

  let air2: HumidAirState;
  if (L <= 0) {
    warn(warnings, 'dryer.noAirflow', 'critical');
    W = 0;
    air2 = air1;
  } else {
    const I2 = air1.I - Qloss / L;
    let Y2 = air1.Y + W / L;
    // I2 = const chizig'ining to'yinish egri chizig'i bilan kesishmasi (adiabatik to'yinish):
    // I(Ts, Ysat(Ts)) = I2. Y2 ≥ Ysat(Ts) bo'lsa havo to'yinadi (T(I2, Y2) ma'nosiz bo'lib qoladi).
    const f = (Ts: number) => humidEnthalpy(Ts, saturationHumidity(Ts, P)) - I2;
    const hi = Math.min(air1.T, saturationTemperature('water', P) - 1e-6);
    const r = bisection(f, 200, hi, { xtol: 1e-9, ftol: 1e-6 });
    diagnostics = {
      converged: r.converged,
      iterations: wb.iterations + r.iterations,
      residual: r.residual,
      ...(r.converged ? {} : { message: r.message ?? 'solver.maxIterations' }),
    };
    const YsatAdiabatic = saturationHumidity(r.value, P);
    let T2: number;
    let phi2: number;
    if (Y2 < YsatAdiabatic) {
      T2 = temperatureFromEnthalpy(I2, Y2);
      phi2 = relativeHumidity(T2, Y2, P, warnings);
    } else {
      T2 = r.value;
      Y2 = YsatAdiabatic;
      phi2 = 1;
      W = Math.max(0, L * (Y2 - air1.Y));
      warn(warnings, 'dryer.saturatedAir', 'warning', {
        X2actual: Number((i.X1 - (G > 0 ? W / G : 0)).toFixed(4)),
      });
    }
    air2 = { T: T2, Y: Y2, phi: phi2, I: I2 };
  }

  const X2actual = G > 0 ? i.X1 - W / G : X2;
  return {
    outputs: {
      mode: 'continuous',
      W,
      X2actual,
      air0,
      air1,
      air2,
      Y1: air1.Y,
      Y2: air2.Y,
      T2: air2.T,
      phi2: air2.phi,
      I1: air1.I,
      I2: air2.I,
      Qheater,
      Qloss,
      specificAir: W > 0 ? L / W : Infinity,
      specificHeat: W > 0 ? Qheater / W : Infinity,
      Twb1: wb.T,
      dewPoint2: dewPoint(air2.Y, P, warnings),
    },
    diagnostics,
  };
}

/** Batch kinetik parametrlar: h, T_wb, N_c, k = N_c·A/G */
export function batchKinetics(
  p: DryerParams,
  i: DryerInputs,
  warnings?: Warning[],
): { h: number; Twb: number; Nc: number; kRate: number; kin: KineticsParams; air1: HumidAirState } {
  const { air1 } = heaterStates(p, i, warnings);
  const h = p.heatTransferCoefficient ?? heatTransferCoefficientTreybal(i.airMassVelocity ?? 0);
  const wb = wetBulbTemperature(air1.T, air1.Y, p.pressure, warnings);
  const Nc = constantDryingRate(h, air1.T, wb.T, heatOfVaporization('water', wb.T, warnings));
  const A = p.dryingArea ?? 0;
  const kRate = i.drySolid > 0 ? (Nc * A) / i.drySolid : 0;
  const kin: KineticsParams = { Nc, Xc: p.criticalMoisture ?? 0, Xeq: p.equilibriumMoisture ?? 0 };
  return { h, Twb: wb.T, Nc, kRate, kin, air1 };
}

export function solveBatchDesign(
  p: DryerParams,
  i: DryerInputs,
  warnings: Warning[],
): { outputs: DryerBatchDesignOutputs; diagnostics: SolverDiagnostics } {
  const { Qheater } = heaterStates(p, i, warnings);
  const { h, Twb, Nc, kRate, kin, air1 } = batchKinetics(p, i, warnings);
  if (p.maxMaterialTemperature !== undefined && i.T1 > p.maxMaterialTemperature)
    warn(warnings, 'dryer.overheating', 'warning', {
      T1: Math.round(i.T1),
      Tmax: Math.round(p.maxMaterialTemperature),
    });
  let X2 = i.X2;
  if (X2 < kin.Xeq) {
    warn(warnings, 'dryer.belowEquilibrium', 'warning', { X2: i.X2, Xeq: kin.Xeq });
    X2 = kin.Xeq;
  }
  if (!(Nc > 0)) warn(warnings, 'dryer.noDrying', 'info');
  const tc = i.X1 > kin.Xc ? timeToMoisture(i.X1, kin.Xc, kin, kRate) : 0;
  const tf = timeToMoisture(Math.min(i.X1, kin.Xc), X2, kin, kRate);
  return {
    outputs: {
      mode: 'batch',
      Nc,
      h,
      Twb,
      constantPeriodTime: tc,
      fallingPeriodTime: tf,
      totalTime: tc + tf,
      totalEvaporated: Math.max(0, i.drySolid * (i.X1 - X2)),
      Qheater,
      Y1: air1.Y,
      I1: air1.I,
    },
    diagnostics: OK,
  };
}

export function solveSteady(p: DryerParams, i: DryerInputs): SteadyResult<DryerSteadyOutputs> {
  validateParams(p);
  validateInputs(p, i);
  getSubstance('water');
  const warnings: Warning[] = [];
  const r = p.mode === 'batch' ? solveBatchDesign(p, i, warnings) : solveContinuous(p, i, warnings);
  return { outputs: r.outputs, warnings, diagnostics: r.diagnostics };
}
